import { api } from "../base";

export interface GroupDestination {
  group_name: string;
  alert_dest_list: { alert_dest_type: string; value: string }[];
}

const alertEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /** Fetches alert check form schemas. */
    getAlertSchemas: build.query<Record<string, unknown> | null, void>({
      query: () => ({ url: "/api/alert/v1/check/forms" }),
      transformResponse: (response: Record<string, unknown>): Record<string, unknown> | null =>
        (response?.alert_checks as Record<string, unknown>) ?? response ?? null,
    }),

    /** Fetches all active alert instances. */
    getAlertInstances: build.query<unknown, void>({
      query: () => ({ url: "/api/alert/v1/instances/all" }),
      providesTags: ["AlertChecks"],
    }),

    /** Fetches portfolios configured for alerts. */
    getAlertPortfolios: build.query<string[], void>({
      query: () => ({ url: "/api/alert/v1/portfolios" }),
      transformResponse: (response: string[] | unknown) =>
        Array.isArray(response) ? response : [],
    }),

    /** Fetches Slack destination channels. */
    getAlertSlackChannels: build.query<string[], void>({
      query: () => ({ url: "/api/alert/v1/check/destinations/slack" }),
      transformResponse: (response: string[] | unknown) =>
        Array.isArray(response) ? response : [],
      providesTags: ["AlertDestinations"],
    }),

    /** Fetches Telegram destination channels. */
    getAlertTelegramChannels: build.query<string[], void>({
      query: () => ({ url: "/api/alert/v1/check/destinations/telegram" }),
      transformResponse: (response: string[] | unknown) =>
        Array.isArray(response) ? response : [],
      providesTags: ["AlertDestinations"],
    }),

    /** Fetches SMS destination channels. */
    getAlertSmsChannels: build.query<string[], void>({
      query: () => ({ url: "/api/alert/v1/check/destinations/sms" }),
      transformResponse: (response: string[] | unknown) =>
        Array.isArray(response) ? response : [],
      providesTags: ["AlertDestinations"],
    }),

    /** Fetches group destinations. */
    getAlertGroupDestinations: build.query<GroupDestination[], void>({
      query: () => ({ url: "/api/alert/v1/check/group_destinations" }),
      transformResponse: (response: GroupDestination[] | unknown) =>
        Array.isArray(response) ? response : [],
      providesTags: ["AlertGroupDestinations"],
    }),

    /** Fetches auth keys for the current user. */
    getAuthKeys: build.query<Record<string, unknown>, void>({
      query: () => ({ url: "/api/auth/v1/key" }),
    }),

    /** Starts an alert check. */
    startAlert: build.mutation<void, Record<string, unknown>>({
      query: (payload) => ({
        url: "/api/alert/v1/check/start",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["AlertChecks"],
    }),

    /** Test-fires an alert check by name. */
    testFireAlert: build.mutation<void, string>({
      query: (checkName) => ({
        url: "/api/alert/v1/check/single_handle",
        method: "POST",
        body: { check_name: checkName.trim() },
      }),
    }),

    /** Stops (deletes) an alert check by name. */
    stopAlert: build.mutation<void, string>({
      query: (alertName) => ({
        url: "/api/alert/v1/check",
        method: "DELETE",
        body: { check_name: alertName },
      }),
      invalidatesTags: ["AlertChecks"],
    }),

    /** Starts default alert config for an account. */
    startDefaultAlertConfig: build.mutation<void, string>({
      query: (accountId) => ({
        url: `/api/alert/v1/check/default/${accountId}/start`,
        method: "POST",
      }),
      invalidatesTags: ["AlertChecks"],
    }),

    /** Creates a group destination. */
    createGroupDestination: build.mutation<void, GroupDestination>({
      query: (payload) => ({
        url: "/api/alert/v1/check/group_destinations",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["AlertGroupDestinations"],
    }),
  }),
});

export const {
  useGetAlertSchemasQuery,
  useGetAlertInstancesQuery,
  useGetAlertPortfoliosQuery,
  useGetAlertSlackChannelsQuery,
  useGetAlertTelegramChannelsQuery,
  useGetAlertSmsChannelsQuery,
  useGetAlertGroupDestinationsQuery,
  useGetAuthKeysQuery,
  useStartAlertMutation,
  useTestFireAlertMutation,
  useStopAlertMutation,
  useStartDefaultAlertConfigMutation,
  useCreateGroupDestinationMutation,
} = alertEndpoints;
